import {CommonModule} from '@angular/common';
import {Component, OnInit, signal} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
  MatAutocompleteModule
} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {SharedFilterModule} from '@progress/kendo-angular-grid';
import {FilterOperator} from '@progress/kendo-data-query';
import {
  Q9TrimValueDirective,
  RefModelStoreService,
  SnackBarService,
  User,
  handleError
} from '@q9elements/ui-kit/common';
import {
  FilterDescriptorWithMetadata,
  FilterLogic,
  GridFilterOperatorSelectComponent,
  UserInfoComponent,
  pickFilterOperators
} from '@q9elements/ui-kit/components';
import {find} from 'lodash';
import {
  EMPTY,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  take,
  tap
} from 'rxjs';
import {RefModelService} from '../../../../../services/ref-model.service';
import {RmeBaseGridFilterComponent} from '../rme-base-grid-filter/rme-base-grid-filter.component';

export interface UnassignedUser {
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
}

const UNASSIGNED = 'Unassigned';

@Component({
  standalone: true,
  selector: 'q9rme-user-filter',
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: {
        overlayPanelClass: 'in-column-overlay-pane'
      }
    }
  ],
  imports: [
    CommonModule,
    MatInputModule,
    MatAutocompleteModule,
    FormsModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    Q9TrimValueDirective,
    UserInfoComponent,
    SharedFilterModule,
    MatSelectModule,
    TranslateModule,
    GridFilterOperatorSelectComponent,
    MatTooltipModule
  ],
  templateUrl: './user-filter.component.html',
  styleUrls: ['./user-filter.component.scss']
})
export class UserFilterComponent extends RmeBaseGridFilterComponent implements OnInit {
  public showUnassigned: boolean;
  private unassignedUser: UnassignedUser = {email: 'unassigned', fullName: 'Unassigned'};

  filterForm = new FormGroup({
    operator: new FormControl(FilterOperator.EqualTo),
    value: new FormControl<User | string>('')
  });

  public readonly operators = pickFilterOperators([
    FilterOperator.EqualTo,
    FilterOperator.NotEqualTo
  ]);

  public readonly isLoading = signal(false);
  public readonly users = signal<(User | UnassignedUser)[]>([]);

  constructor(
    private snackBarService: SnackBarService,
    private refModelService: RefModelService,
    private store: RefModelStoreService
  ) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();

    this.initSearchControlObserver();
  }

  public formatAssignee = (value: User | string | null): string => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      const user = this.users().find(user => user.email === value);

      return user ? this.getDisplayName(user) : value;
    }

    return this.getDisplayName(value);
  };

  private getDisplayName(user: User | UnassignedUser): string {
    return user.firstName || user.lastName
      ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
      : user.email;
  }

  protected override initFilter(): void {
    this.isLoading.set(true);
    const currentFilter = find(this.filter.filters, {
      field: this.field
    }) as FilterDescriptorWithMetadata;

    if (!currentFilter) {
      return void 0;
    }

    const {value, metadata = {}} = currentFilter;

    let {operator} = currentFilter;

    if (!operator) {
      operator = FilterOperator.EqualTo;
    }

    this.filterForm.patchValue({operator: <FilterOperator>operator});

    let selectedUser: User | string = metadata[this.field] || value;

    if (typeof selectedUser === 'object') {
      return this.filterForm.patchValue({value: selectedUser});
    }

    this.fetchUsers()
      .pipe(take(1))
      .subscribe(users => {
        if (value.toLowerCase === UNASSIGNED) {
          users.push(this.unassignedUser);
        }
        this.users.set(users);

        selectedUser = (find(users, {email: selectedUser}) as User) || '';

        if (selectedUser) {
          this.filterForm.patchValue({value: selectedUser});
        }

        this.isLoading.set(false);
      });
  }

  protected override initFilterChangesObserver() {
    this.filterForm.valueChanges
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(({operator, value}) => {
        const user = value as User | UnassignedUser;

        this.applyFilter(user.email, <FilterOperator>operator, {
          [this.field]: user
        });
      });
  }

  protected override applyFilter(
    value: string,
    operator: FilterOperator | FilterOperator[],
    metadata?: any
  ) {
    this.filterService.filter({
      filters: [
        {
          metadata,
          field: this.field,
          operator: operator,
          value
        } as FilterDescriptorWithMetadata
      ],
      logic: FilterLogic.AND
    });
  }

  private initSearchControlObserver() {
    this.filterForm
      .get('value')!
      .valueChanges.pipe(
        this.takeUntilComponentDestroy(),
        filter(search => typeof search === 'string' && search!.length > 1),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(search => this.fetchUsers(<string>search!))
      )
      .subscribe(users => this.users.set(users));
  }

  private fetchUsers(search?: string) {
    this.isLoading.set(true);
    let pattern = search || '';
    const refModel = this.store.refModel;

    this.showUnassigned = RegExp(search, 'i').test(UNASSIGNED);
    if (this.showUnassigned) {
      pattern = '';
    }
    return this.refModelService.getOptimizeAssignees(refModel._id, pattern).pipe(
      handleError(),
      tap(() => {
        this.isLoading.set(false);
      }),
      tap(response => {
        if (this.showUnassigned) {
          response.push(this.unassignedUser);
        }

        return response;
      }),
      catchError(error => {
        this.snackBarService.open(error);
        this.isLoading.set(false);

        return EMPTY;
      })
    );
  }
}
